import { ErrorCode } from '@spare-cart/contracts';
import { z } from 'zod';
import { CouponStatus, CouponRejectionReason, CouponType } from '@spare-cart/contracts/promotions';
import { UserRole } from '@spare-cart/contracts/auth';
import { OrderStatus } from '@spare-cart/contracts/orders';

// src/errors/app-error.ts
var HTTP_STATUS_BY_ERROR_CODE = {
  [ErrorCode.VALIDATION_ERROR]: 400,
  [ErrorCode.UNAUTHORIZED]: 401,
  [ErrorCode.PAYMENT_FAILED]: 402,
  [ErrorCode.FORBIDDEN]: 403,
  [ErrorCode.NOT_FOUND]: 404,
  [ErrorCode.CONFLICT]: 409,
  [ErrorCode.RATE_LIMITED]: 429,
  [ErrorCode.INTERNAL_ERROR]: 500
};
var REDACTED_MESSAGE = "Something went wrong. Please try again.";
var AppError = class extends Error {
  code;
  status;
  details;
  expose;
  retryAfterSeconds;
  constructor(code, message, options = {}) {
    super(message, options.cause === void 0 ? void 0 : { cause: options.cause });
    this.name = new.target.name;
    this.code = code;
    this.status = options.status ?? HTTP_STATUS_BY_ERROR_CODE[code];
    this.details = options.details;
    this.expose = options.expose ?? this.status < 500;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }
};
var ValidationError = class extends AppError {
  constructor(message = "The request payload is invalid.", details, options = {}) {
    super(ErrorCode.VALIDATION_ERROR, message, { ...options, details: details ?? options.details });
  }
};
var UnauthorizedError = class extends AppError {
  constructor(message = "Authentication is required.", options = {}) {
    super(ErrorCode.UNAUTHORIZED, message, options);
  }
};
var ForbiddenError = class extends AppError {
  constructor(message = "You do not have access to this resource.", options = {}) {
    super(ErrorCode.FORBIDDEN, message, options);
  }
};
var NotFoundError = class extends AppError {
  constructor(message = "The requested resource does not exist.", options = {}) {
    super(ErrorCode.NOT_FOUND, message, options);
  }
};
var ConflictError = class extends AppError {
  constructor(message = "The request conflicts with the current state.", options = {}) {
    super(ErrorCode.CONFLICT, message, options);
  }
};
var RateLimitedError = class extends AppError {
  constructor(message = "Too many requests. Please slow down.", retryAfterSeconds, options = {}) {
    super(ErrorCode.RATE_LIMITED, message, {
      ...options,
      retryAfterSeconds: retryAfterSeconds ?? options.retryAfterSeconds
    });
  }
};
var PaymentFailedError = class extends AppError {
  constructor(message = "The payment could not be completed.", options = {}) {
    super(ErrorCode.PAYMENT_FAILED, message, options);
  }
};
var InternalError = class extends AppError {
  constructor(message = "Unexpected server error.", options = {}) {
    super(ErrorCode.INTERNAL_ERROR, message, { expose: false, ...options });
  }
};
var isAppError = (value) => value instanceof AppError;
var notFound = (entity, identifier) => new NotFoundError(
  identifier == null ? `${entity} was not found.` : `${entity} "${identifier}" was not found.`
);
var toErrorBody = (error, requestId, redact = true) => {
  if (!isAppError(error)) {
    return {
      code: ErrorCode.INTERNAL_ERROR,
      message: redact ? REDACTED_MESSAGE : String(error),
      requestId
    };
  }
  const message = error.expose || !redact ? error.message : REDACTED_MESSAGE;
  return error.details == null ? { code: error.code, message, requestId } : { code: error.code, message, details: [...error.details], requestId };
};

// src/errors/from-zod.ts
var formatIssuePath = (path) => path.reduce((acc, segment) => {
  if (typeof segment === "number") return `${acc}[${segment}]`;
  return acc.length === 0 ? String(segment) : `${acc}.${String(segment)}`;
}, "");
var zodIssuesToDetails = (error) => error.issues.map((issue) => ({ path: formatIssuePath(issue.path), message: issue.message }));
var validationErrorFromZod = (error, message = "The request payload is invalid.") => new ValidationError(message, zodIssuesToDetails(error));

// src/verticals/types.ts
var AttributeKind = {
  STRING: "string",
  NUMBER: "number",
  BOOLEAN: "boolean",
  ENUM: "enum"
};
var fuelTypes = [
  "petrol",
  "diesel",
  "cng",
  "lpg",
  "hybrid",
  "petrol_cng",
  "petrol_lpg",
  "petrol_diesel"
];
var transmissions = ["manual", "automatic", "amt", "cvt", "dct"];
var driveTypes = ["fwd", "rwd", "awd", "4wd"];
var car = {
  slug: "car",
  name: "Car",
  sortOrder: 10,
  labels: {
    singular: "Car",
    plural: "Cars",
    make: "Make",
    model: "Model",
    variant: "Variant",
    garage: "My Garage",
    selectPrompt: "Select your car",
    fitmentTitle: "Fits these cars"
  },
  attributes: [
    {
      key: "fuelType",
      label: "Fuel type",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "petrol", label: "Petrol" },
        { value: "diesel", label: "Diesel" },
        { value: "cng", label: "CNG" },
        { value: "lpg", label: "LPG" },
        { value: "hybrid", label: "Hybrid" },
        { value: "petrol_cng", label: "Petrol + CNG" },
        { value: "petrol_lpg", label: "Petrol + LPG" },
        { value: "petrol_diesel", label: "Petrol + Diesel" }
      ]
    },
    {
      key: "transmission",
      label: "Transmission",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "manual", label: "Manual" },
        { value: "automatic", label: "Automatic" },
        { value: "amt", label: "AMT" },
        { value: "cvt", label: "CVT" },
        { value: "dct", label: "DCT" }
      ]
    },
    {
      key: "driveType",
      label: "Drive type",
      kind: AttributeKind.ENUM,
      filterable: false,
      options: [
        { value: "fwd", label: "Front wheel drive" },
        { value: "rwd", label: "Rear wheel drive" },
        { value: "awd", label: "All wheel drive" },
        { value: "4wd", label: "Four wheel drive" }
      ]
    },
    {
      key: "engineCc",
      label: "Engine capacity",
      kind: AttributeKind.NUMBER,
      filterable: false,
      unit: "cc"
    },
    { key: "engineCode", label: "Engine code", kind: AttributeKind.STRING, filterable: false }
  ],
  attributesSchema: z.object({
    fuelType: z.enum(fuelTypes).optional(),
    transmission: z.enum(transmissions).optional(),
    driveType: z.enum(driveTypes).optional(),
    engineCc: z.number().int().positive().max(12e3).optional(),
    engineCode: z.string().trim().min(1).max(40).optional()
  })
};
var fuelTypes2 = ["petrol", "electric"];
var strokeTypes = ["two_stroke", "four_stroke"];
var coolingTypes = ["air", "oil", "liquid"];
var startTypes = ["kick", "electric", "kick_and_electric"];
var motorcycle = {
  slug: "motorcycle",
  name: "Motorcycle",
  sortOrder: 20,
  labels: {
    singular: "Motorcycle",
    plural: "Motorcycles",
    make: "Brand",
    model: "Model",
    variant: "Variant",
    garage: "My Garage",
    selectPrompt: "Select your motorcycle",
    fitmentTitle: "Fits these motorcycles"
  },
  attributes: [
    {
      key: "engineCc",
      label: "Engine capacity",
      kind: AttributeKind.NUMBER,
      filterable: true,
      unit: "cc"
    },
    {
      key: "strokeType",
      label: "Stroke",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "two_stroke", label: "2-stroke" },
        { value: "four_stroke", label: "4-stroke" }
      ]
    },
    {
      key: "fuelType",
      label: "Fuel type",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "petrol", label: "Petrol" },
        { value: "electric", label: "Electric" }
      ]
    },
    {
      key: "coolingType",
      label: "Cooling",
      kind: AttributeKind.ENUM,
      filterable: false,
      options: [
        { value: "air", label: "Air cooled" },
        { value: "oil", label: "Oil cooled" },
        { value: "liquid", label: "Liquid cooled" }
      ]
    },
    {
      key: "startType",
      label: "Start",
      kind: AttributeKind.ENUM,
      filterable: false,
      options: [
        { value: "kick", label: "Kick start" },
        { value: "electric", label: "Electric start" },
        { value: "kick_and_electric", label: "Kick and electric" }
      ]
    },
    { key: "gearCount", label: "Gears", kind: AttributeKind.NUMBER, filterable: false }
  ],
  attributesSchema: z.object({
    engineCc: z.number().int().positive().max(3e3).optional(),
    strokeType: z.enum(strokeTypes).optional(),
    fuelType: z.enum(fuelTypes2).optional(),
    coolingType: z.enum(coolingTypes).optional(),
    startType: z.enum(startTypes).optional(),
    gearCount: z.number().int().min(0).max(8).optional()
  })
};
var fuelTypes3 = ["petrol", "diesel", "cng", "lpg", "electric"];
var applications = ["passenger", "cargo"];
var threeWheeler = {
  slug: "three-wheeler",
  name: "Three wheeler",
  sortOrder: 50,
  labels: {
    singular: "Three wheeler",
    plural: "Three wheelers",
    make: "Brand",
    model: "Model",
    variant: "Variant",
    garage: "My Vehicles",
    selectPrompt: "Select your three wheeler",
    fitmentTitle: "Fits these three wheelers"
  },
  attributes: [
    {
      key: "fuelType",
      label: "Fuel type",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "petrol", label: "Petrol" },
        { value: "diesel", label: "Diesel" },
        { value: "cng", label: "CNG" },
        { value: "lpg", label: "LPG" },
        { value: "electric", label: "Electric" }
      ]
    },
    {
      key: "application",
      label: "Application",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "passenger", label: "Passenger" },
        { value: "cargo", label: "Cargo" }
      ]
    },
    {
      key: "engineCc",
      label: "Engine capacity",
      kind: AttributeKind.NUMBER,
      filterable: false,
      unit: "cc"
    },
    {
      key: "seatingCapacity",
      label: "Seating capacity",
      kind: AttributeKind.NUMBER,
      filterable: false
    },
    {
      key: "payloadKg",
      label: "Payload",
      kind: AttributeKind.NUMBER,
      filterable: false,
      unit: "kg"
    }
  ],
  attributesSchema: z.object({
    fuelType: z.enum(fuelTypes3).optional(),
    application: z.enum(applications).optional(),
    engineCc: z.number().int().positive().max(1500).optional(),
    seatingCapacity: z.number().int().min(1).max(12).optional(),
    payloadKg: z.number().int().positive().max(2e3).optional()
  })
};
var fuelTypes4 = ["diesel", "electric"];
var driveTypes2 = ["two_wheel_drive", "four_wheel_drive"];
var tractor = {
  slug: "tractor",
  name: "Tractor",
  sortOrder: 40,
  labels: {
    singular: "Tractor",
    plural: "Tractors",
    make: "Brand",
    model: "Series",
    variant: "Model",
    garage: "My Tractors",
    selectPrompt: "Select your tractor",
    fitmentTitle: "Fits these tractors"
  },
  attributes: [
    {
      key: "horsepower",
      label: "Engine power",
      kind: AttributeKind.NUMBER,
      filterable: true,
      unit: "hp"
    },
    {
      key: "driveType",
      label: "Drive",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "two_wheel_drive", label: "2WD" },
        { value: "four_wheel_drive", label: "4WD" }
      ]
    },
    {
      key: "fuelType",
      label: "Fuel type",
      kind: AttributeKind.ENUM,
      filterable: false,
      options: [
        { value: "diesel", label: "Diesel" },
        { value: "electric", label: "Electric" }
      ]
    },
    { key: "cylinders", label: "Cylinders", kind: AttributeKind.NUMBER, filterable: false },
    {
      key: "ptoHorsepower",
      label: "PTO power",
      kind: AttributeKind.NUMBER,
      filterable: false,
      unit: "hp"
    },
    {
      key: "engineCc",
      label: "Engine capacity",
      kind: AttributeKind.NUMBER,
      filterable: false,
      unit: "cc"
    }
  ],
  attributesSchema: z.object({
    horsepower: z.number().positive().max(700).optional(),
    driveType: z.enum(driveTypes2).optional(),
    fuelType: z.enum(fuelTypes4).optional(),
    cylinders: z.number().int().min(1).max(12).optional(),
    ptoHorsepower: z.number().positive().max(700).optional(),
    engineCc: z.number().int().positive().max(2e4).optional()
  })
};
var fuelTypes5 = ["diesel", "cng", "lng", "electric"];
var axleConfigurations = ["4x2", "6x2", "6x4", "8x2", "8x4", "10x2", "12x2"];
var cabTypes = ["day_cab", "sleeper_cab", "crew_cab"];
var applications2 = ["haulage", "tipper", "tanker", "trailer", "mixer"];
var truck = {
  slug: "truck",
  name: "Truck",
  sortOrder: 30,
  labels: {
    singular: "Truck",
    plural: "Trucks",
    make: "Make",
    model: "Model",
    variant: "Configuration",
    garage: "My Fleet",
    selectPrompt: "Select your truck",
    fitmentTitle: "Fits these trucks"
  },
  attributes: [
    {
      key: "fuelType",
      label: "Fuel type",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "diesel", label: "Diesel" },
        { value: "cng", label: "CNG" },
        { value: "lng", label: "LNG" },
        { value: "electric", label: "Electric" }
      ]
    },
    {
      key: "axleConfiguration",
      label: "Axle configuration",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: axleConfigurations.map((value) => ({ value, label: value.toUpperCase() }))
    },
    {
      key: "application",
      label: "Application",
      kind: AttributeKind.ENUM,
      filterable: true,
      options: [
        { value: "haulage", label: "Haulage" },
        { value: "tipper", label: "Tipper" },
        { value: "tanker", label: "Tanker" },
        { value: "trailer", label: "Trailer" },
        { value: "mixer", label: "Mixer" }
      ]
    },
    {
      key: "cabType",
      label: "Cab type",
      kind: AttributeKind.ENUM,
      filterable: false,
      options: [
        { value: "day_cab", label: "Day cab" },
        { value: "sleeper_cab", label: "Sleeper cab" },
        { value: "crew_cab", label: "Crew cab" }
      ]
    },
    {
      key: "grossWeightKg",
      label: "Gross vehicle weight",
      kind: AttributeKind.NUMBER,
      filterable: false,
      unit: "kg"
    },
    { key: "engineCode", label: "Engine code", kind: AttributeKind.STRING, filterable: false }
  ],
  attributesSchema: z.object({
    fuelType: z.enum(fuelTypes5).optional(),
    axleConfiguration: z.enum(axleConfigurations).optional(),
    application: z.enum(applications2).optional(),
    cabType: z.enum(cabTypes).optional(),
    grossWeightKg: z.number().int().positive().max(12e4).optional(),
    engineCode: z.string().trim().min(1).max(40).optional()
  })
};

// src/verticals/registry.ts
var definitions = [car, motorcycle, truck, tractor, threeWheeler];
var VERTICALS = [...definitions].sort(
  (a, b) => a.sortOrder - b.sortOrder || a.slug.localeCompare(b.slug)
);
var VERTICAL_SLUGS = VERTICALS.map((vertical) => vertical.slug);
var bySlug = new Map(VERTICALS.map((vertical) => [vertical.slug, vertical]));
var getVertical = (slug) => bySlug.get(slug);
var isVerticalSlug = (slug) => bySlug.has(slug);
var requireVertical = (slug) => {
  const vertical = getVertical(slug);
  if (vertical == null) throw new NotFoundError(`Unknown vehicle type "${slug}".`);
  return vertical;
};
var listVerticals = () => VERTICALS;
var enabledVerticals = (enabledSlugs) => VERTICALS.filter((vertical) => enabledSlugs.includes(vertical.slug));
var showsVehicleTypeSelector = (enabledSlugs) => enabledVerticals(enabledSlugs).length > 1;
var defaultVertical = (enabledSlugs) => enabledVerticals(enabledSlugs)[0];
var assertEnabledVerticals = (enabledSlugs) => {
  const unknown = enabledSlugs.filter((slug) => !bySlug.has(slug));
  if (unknown.length > 0) {
    throw new ValidationError(`Unknown vehicle type slugs: ${unknown.join(", ")}.`);
  }
  if (enabledSlugs.length === 0) {
    throw new ValidationError("At least one vehicle type must be enabled.");
  }
};
var verticalAttributes = (slug) => requireVertical(slug).attributes;
var filterableAttributes = (slug) => requireVertical(slug).attributes.filter((attribute) => attribute.filterable);
var isFilterableAttribute = (slug, key) => filterableAttributes(slug).some((attribute) => attribute.key === key);
var safeParseVehicleAttributes = (slug, attributes) => {
  const result = requireVertical(slug).attributesSchema.safeParse(attributes ?? {});
  if (result.success) return { ok: true, value: result.data };
  return { ok: false, issues: validationErrorFromZod(result.error).details?.slice() ?? [] };
};
var parseVehicleAttributes = (slug, attributes) => {
  const result = requireVertical(slug).attributesSchema.safeParse(attributes ?? {});
  if (!result.success) {
    throw validationErrorFromZod(result.error, `Invalid attributes for vehicle type "${slug}".`);
  }
  return result.data;
};

// src/fitment/types.ts
var FitmentReason = {
  VEHICLE_TYPE_MISMATCH: "vehicle_type_mismatch",
  MAKE_MISMATCH: "make_mismatch",
  MODEL_MISMATCH: "model_mismatch",
  VARIANT_MISMATCH: "variant_mismatch",
  VARIANT_UNKNOWN: "variant_unknown",
  YEAR_OUT_OF_RANGE: "year_out_of_range",
  YEAR_UNKNOWN: "year_unknown",
  NO_FITMENTS: "no_fitments"
};

// src/fitment/match.ts
var SPECIFICITY = { make: 1, model: 2, variant: 4, year: 1 };
var REASON_DEPTH = {
  [FitmentReason.NO_FITMENTS]: 0,
  [FitmentReason.VEHICLE_TYPE_MISMATCH]: 1,
  [FitmentReason.MAKE_MISMATCH]: 2,
  [FitmentReason.MODEL_MISMATCH]: 3,
  [FitmentReason.VARIANT_MISMATCH]: 4,
  [FitmentReason.VARIANT_UNKNOWN]: 4,
  [FitmentReason.YEAR_OUT_OF_RANGE]: 5,
  [FitmentReason.YEAR_UNKNOWN]: 5
};
var hasYearConstraint = (range) => range.yearFrom != null || range.yearTo != null;
var yearRangesOverlap = (a, b) => {
  const aFrom = a.yearFrom ?? Number.NEGATIVE_INFINITY;
  const aTo = a.yearTo ?? Number.POSITIVE_INFINITY;
  const bFrom = b.yearFrom ?? Number.NEGATIVE_INFINITY;
  const bTo = b.yearTo ?? Number.POSITIVE_INFINITY;
  return aFrom <= bTo && bFrom <= aTo;
};
var fitmentSpecificity = (rule) => (rule.makeId != null ? SPECIFICITY.make : 0) + (rule.modelId != null ? SPECIFICITY.model : 0) + (rule.variantId != null ? SPECIFICITY.variant : 0) + (hasYearConstraint(rule) ? SPECIFICITY.year : 0);
var noFit = (reason) => ({ fits: false, reason });
var matchHierarchy = (rule, vehicle) => {
  if (rule.vehicleTypeId !== vehicle.vehicleTypeId) return FitmentReason.VEHICLE_TYPE_MISMATCH;
  if (rule.makeId != null && rule.makeId !== vehicle.makeId) return FitmentReason.MAKE_MISMATCH;
  if (rule.modelId != null && rule.modelId !== vehicle.modelId) return FitmentReason.MODEL_MISMATCH;
  if (rule.variantId != null) {
    if (vehicle.variantId == null) return FitmentReason.VARIANT_UNKNOWN;
    if (rule.variantId !== vehicle.variantId) return FitmentReason.VARIANT_MISMATCH;
  }
  return null;
};
var matchFitment = (rule, vehicle) => {
  const hierarchyReason = matchHierarchy(rule, vehicle);
  if (hierarchyReason != null) return noFit(hierarchyReason);
  if (hasYearConstraint(rule)) {
    if (vehicle.year == null) return noFit(FitmentReason.YEAR_UNKNOWN);
    if (!yearRangesOverlap(rule, { yearFrom: vehicle.year, yearTo: vehicle.year })) {
      return noFit(FitmentReason.YEAR_OUT_OF_RANGE);
    }
  }
  return { fits: true, specificity: fitmentSpecificity(rule) };
};
var matchFitmentToVariant = (rule, variant) => {
  const hierarchyReason = matchHierarchy(rule, variant);
  if (hierarchyReason != null) return noFit(hierarchyReason);
  if (hasYearConstraint(rule) && !yearRangesOverlap(rule, variant)) {
    return noFit(FitmentReason.YEAR_OUT_OF_RANGE);
  }
  return { fits: true, specificity: fitmentSpecificity(rule) };
};
var checkFitment = (rules, vehicle) => {
  if (rules.length === 0) {
    return { fits: false, matchedRule: null, reason: FitmentReason.NO_FITMENTS };
  }
  let best = null;
  let closestReason = FitmentReason.VEHICLE_TYPE_MISMATCH;
  for (const rule of rules) {
    const match = matchFitment(rule, vehicle);
    if (match.fits) {
      if (best == null || match.specificity > best.specificity) {
        best = { rule, specificity: match.specificity };
      }
      continue;
    }
    if (REASON_DEPTH[match.reason] > REASON_DEPTH[closestReason]) closestReason = match.reason;
  }
  if (best != null) return { fits: true, matchedRule: best.rule, reason: null };
  return { fits: false, matchedRule: null, reason: closestReason };
};
var fitsVehicle = (rules, vehicle) => checkFitment(rules, vehicle).fits;
var isIndeterminate = (reason) => reason === FitmentReason.VARIANT_UNKNOWN || reason === FitmentReason.YEAR_UNKNOWN;

// src/pricing/money.ts
var MONEY_PATTERN = /^-?\d{1,12}(?:\.\d{1,2})?$/;
var assertInteger = (value, label) => {
  if (!Number.isSafeInteger(value)) {
    throw new ValidationError(`${label} must be a safe integer, received ${value}.`);
  }
  return value;
};
var parseMoney = (value) => {
  const trimmed = value.trim();
  if (!MONEY_PATTERN.test(trimmed)) {
    throw new ValidationError(`"${value}" is not a valid decimal money value.`);
  }
  const negative = trimmed.startsWith("-");
  const [whole = "0", fraction = ""] = (negative ? trimmed.slice(1) : trimmed).split(".");
  const paise = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return assertInteger(negative ? -paise : paise, "Money");
};
var parseOptionalMoney = (value) => value == null ? null : parseMoney(value);
var formatMoney = (paise) => {
  assertInteger(paise, "Money");
  const absolute = Math.abs(paise);
  const whole = Math.trunc(absolute / 100);
  const fraction = absolute % 100;
  return `${paise < 0 ? "-" : ""}${whole}.${String(fraction).padStart(2, "0")}`;
};
var sumPaise = (values) => values.reduce((total, value) => total + assertInteger(value, "Money"), 0);
var clampToZero = (paise) => paise < 0 ? 0 : paise;
var minPaise = (a, b) => a < b ? a : b;
var mulDivRound = (value, numerator, denominator) => {
  assertInteger(value, "Value");
  assertInteger(numerator, "Numerator");
  assertInteger(denominator, "Denominator");
  if (denominator === 0) throw new ValidationError("Cannot divide by zero.");
  const product = BigInt(value) * BigInt(numerator);
  const divisor = BigInt(denominator);
  const negative = product < 0n !== divisor < 0n;
  const absoluteProduct = product < 0n ? -product : product;
  const absoluteDivisor = divisor < 0n ? -divisor : divisor;
  const quotient = absoluteProduct / absoluteDivisor;
  const remainder = absoluteProduct % absoluteDivisor;
  const rounded = remainder * 2n >= absoluteDivisor ? quotient + 1n : quotient;
  const result = Number(negative ? -rounded : rounded);
  return assertInteger(result, "Rounded value");
};
var BASIS_POINTS = 1e4;
var percentToBasisPoints = (percent) => {
  if (!Number.isFinite(percent) || percent < 0) {
    throw new ValidationError(`"${percent}" is not a valid percentage.`);
  }
  return Math.round(percent * 100);
};
var applyPercent = (paise, percent) => mulDivRound(paise, percentToBasisPoints(percent), BASIS_POINTS);
var extractInclusiveTax = (grossPaise, percent) => {
  const basisPoints = percentToBasisPoints(percent);
  const net = mulDivRound(grossPaise, BASIS_POINTS, BASIS_POINTS + basisPoints);
  return grossPaise - net;
};

// src/pricing/allocate.ts
var allocateProportionally = (total, weights) => {
  if (weights.length === 0) return [];
  if (!Number.isSafeInteger(total)) {
    throw new ValidationError(`Allocation total must be a safe integer, received ${total}.`);
  }
  if (weights.some((weight) => !Number.isSafeInteger(weight) || weight < 0)) {
    throw new ValidationError("Allocation weights must be non-negative safe integers.");
  }
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  const shares = weightSum === 0 ? weights.map(() => 1) : weights;
  const divisor = BigInt(weightSum === 0 ? weights.length : weightSum);
  const magnitude = BigInt(Math.abs(total));
  const parts = shares.map((share) => {
    const scaled = magnitude * BigInt(share);
    return { base: Number(scaled / divisor), remainder: Number(scaled % divisor) };
  });
  const allocation = parts.map((part) => part.base);
  let leftover = Math.abs(total) - allocation.reduce((sum, value) => sum + value, 0);
  const order = parts.map((part, index) => ({ index, remainder: part.remainder })).sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const entry of order) {
    if (leftover <= 0) break;
    allocation[entry.index] = (allocation[entry.index] ?? 0) + 1;
    leftover -= 1;
  }
  const sign = total < 0 ? -1 : 1;
  return allocation.map((value) => value * sign);
};

// src/pricing/lines.ts
var lineGross = (line) => {
  if (!Number.isSafeInteger(line.quantity) || line.quantity < 0) {
    throw new ValidationError(
      `Quantity must be a non-negative integer, received ${line.quantity}.`
    );
  }
  if (!Number.isSafeInteger(line.unitPrice) || line.unitPrice < 0) {
    throw new ValidationError(
      `Unit price must be a non-negative integer, received ${line.unitPrice}.`
    );
  }
  return line.unitPrice * line.quantity;
};
var computeLineTotals = (line, options) => {
  const gross = lineGross(line);
  const discount = minPaise(clampToZero(line.discount ?? 0), gross);
  const net = gross - discount;
  if (options.pricesIncludeTax) {
    const tax2 = extractInclusiveTax(net, line.taxRate);
    return { gross, discount, taxable: net - tax2, tax: tax2, total: net, taxRate: line.taxRate };
  }
  const tax = applyPercent(net, line.taxRate);
  return { gross, discount, taxable: net, tax, total: net + tax, taxRate: line.taxRate };
};

// src/pricing/tax.ts
var groupTax = (lines) => {
  const groups = /* @__PURE__ */ new Map();
  for (const line of lines) {
    const existing = groups.get(line.taxRate);
    if (existing == null) {
      groups.set(line.taxRate, { rate: line.taxRate, taxable: line.taxable, tax: line.tax });
      continue;
    }
    existing.taxable += line.taxable;
    existing.tax += line.tax;
  }
  return [...groups.values()].filter((group) => group.tax !== 0 || group.rate !== 0).sort((a, b) => a.rate - b.rate);
};
var toTaxLines = (groups, label = "GST") => groups.map((group) => ({
  label,
  rate: group.rate,
  taxableAmount: formatMoney(group.taxable),
  amount: formatMoney(group.tax)
}));
var reject = (reason) => ({
  valid: false,
  discount: 0,
  freeShipping: false,
  reason
});
var couponStatusAt = (coupon, now) => {
  if (!coupon.isActive) return CouponStatus.DISABLED;
  if (coupon.usageLimit != null && coupon.usedCount >= coupon.usageLimit) {
    return CouponStatus.EXHAUSTED;
  }
  if (now < coupon.startsAt) return CouponStatus.SCHEDULED;
  if (coupon.endsAt != null && now > coupon.endsAt) return CouponStatus.EXPIRED;
  return CouponStatus.ACTIVE;
};
var couponDiscount = (coupon, eligibleSubtotal) => {
  if (coupon.type === CouponType.FREE_SHIPPING) return 0;
  const raw = coupon.type === CouponType.PERCENTAGE ? applyPercent(eligibleSubtotal, coupon.percentValue ?? 0) : coupon.value;
  const capped = coupon.maxDiscount == null ? raw : minPaise(raw, coupon.maxDiscount);
  return minPaise(capped, eligibleSubtotal);
};
var evaluateCoupon = (coupon, context) => {
  const status = couponStatusAt(coupon, context.now);
  if (status === CouponStatus.DISABLED) return reject(CouponRejectionReason.DISABLED);
  if (status === CouponStatus.EXHAUSTED) return reject(CouponRejectionReason.USAGE_LIMIT_REACHED);
  if (status === CouponStatus.SCHEDULED) return reject(CouponRejectionReason.NOT_STARTED);
  if (status === CouponStatus.EXPIRED) return reject(CouponRejectionReason.EXPIRED);
  if (coupon.perUserLimit != null && context.userRedemptions >= coupon.perUserLimit) {
    return reject(CouponRejectionReason.USER_LIMIT_REACHED);
  }
  if (coupon.minOrderValue != null && context.orderSubtotal < coupon.minOrderValue) {
    return reject(CouponRejectionReason.MIN_ORDER_NOT_MET);
  }
  if (coupon.type !== CouponType.FREE_SHIPPING && context.eligibleSubtotal <= 0) {
    return reject(CouponRejectionReason.NOT_APPLICABLE);
  }
  const discount = couponDiscount(coupon, context.eligibleSubtotal);
  if (coupon.type !== CouponType.FREE_SHIPPING && discount <= 0) {
    return reject(CouponRejectionReason.NOT_APPLICABLE);
  }
  return {
    valid: true,
    discount,
    freeShipping: coupon.type === CouponType.FREE_SHIPPING,
    reason: null
  };
};

// src/pricing/shipping.ts
var totalWeightGrams = (items) => items.reduce((total, item) => total + item.weightGrams * item.quantity, 0);
var resolveWeightBand = (bands, weightGrams) => {
  if (!Number.isFinite(weightGrams) || weightGrams < 0) {
    throw new ValidationError(`Weight must be a non-negative number, received ${weightGrams}.`);
  }
  const candidates = bands.filter(
    (band) => weightGrams >= band.minGrams && (band.maxGrams == null || weightGrams <= band.maxGrams)
  );
  const [best] = [...candidates].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || b.minGrams - a.minGrams
  );
  return best ?? null;
};
var quoteShipping = (input) => {
  const band = resolveWeightBand(input.bands, input.weightGrams);
  const thresholdMet = input.freeShippingAbove != null && input.orderSubtotal >= input.freeShippingAbove;
  const freeShippingApplied = Boolean(input.freeShippingCoupon) || thresholdMet;
  if (band == null) {
    return { charge: 0, band: null, etaDays: null, freeShippingApplied, bandMatched: false };
  }
  return {
    charge: freeShippingApplied ? 0 : band.charge,
    band,
    etaDays: band.etaDays,
    freeShippingApplied,
    bandMatched: true
  };
};

// src/pricing/totals.ts
var netBeforeCoupon = (line) => {
  const gross = lineGross(line);
  return clampToZero(gross - minPaise(clampToZero(line.discount ?? 0), gross));
};
var computeOrderPricing = (input) => {
  const options = { pricesIncludeTax: input.pricesIncludeTax };
  const weights = input.lines.map(
    (line) => line.couponEligible === false ? 0 : netBeforeCoupon(line)
  );
  const eligibleTotal = sumPaise(weights);
  const couponDiscount2 = minPaise(clampToZero(input.couponDiscount ?? 0), eligibleTotal);
  const couponAllocation = allocateProportionally(couponDiscount2, weights);
  const lines = input.lines.map(
    (line, index) => computeLineTotals(
      { ...line, discount: clampToZero(line.discount ?? 0) + (couponAllocation[index] ?? 0) },
      options
    )
  );
  const subtotal = sumPaise(lines.map((line) => line.gross));
  const discount = sumPaise(lines.map((line) => line.discount));
  const taxTotal = sumPaise(lines.map((line) => line.tax));
  const shippingTotal = clampToZero(input.shippingCharge ?? 0);
  const grandTotal = sumPaise(lines.map((line) => line.total)) + shippingTotal;
  return {
    lines,
    couponAllocation,
    subtotal,
    discount,
    couponDiscount: couponDiscount2,
    taxTotal,
    shippingTotal,
    grandTotal,
    taxGroups: groupTax(lines)
  };
};
var toCheckoutTotals = (pricing, currency) => ({
  subtotal: formatMoney(pricing.subtotal),
  discount: formatMoney(pricing.discount),
  taxTotal: formatMoney(pricing.taxTotal),
  shippingTotal: formatMoney(pricing.shippingTotal),
  grandTotal: formatMoney(pricing.grandTotal),
  currency
});
var STAFF = [UserRole.ADMIN, UserRole.SUPER_ADMIN];
var EVERYONE = [UserRole.CUSTOMER, UserRole.ADMIN, UserRole.SUPER_ADMIN];
var ORDER_TRANSITIONS = {
  [OrderStatus.PENDING]: [
    { to: OrderStatus.CONFIRMED, roles: STAFF },
    { to: OrderStatus.CANCELLED, roles: EVERYONE }
  ],
  [OrderStatus.CONFIRMED]: [
    { to: OrderStatus.PACKED, roles: STAFF },
    { to: OrderStatus.CANCELLED, roles: EVERYONE }
  ],
  [OrderStatus.PACKED]: [
    { to: OrderStatus.SHIPPED, roles: STAFF },
    { to: OrderStatus.CANCELLED, roles: STAFF }
  ],
  [OrderStatus.SHIPPED]: [
    { to: OrderStatus.OUT_FOR_DELIVERY, roles: STAFF },
    { to: OrderStatus.RETURNED, roles: STAFF }
  ],
  [OrderStatus.OUT_FOR_DELIVERY]: [
    { to: OrderStatus.DELIVERED, roles: STAFF },
    { to: OrderStatus.RETURNED, roles: STAFF }
  ],
  [OrderStatus.DELIVERED]: [{ to: OrderStatus.RETURNED, roles: EVERYONE }],
  [OrderStatus.CANCELLED]: [{ to: OrderStatus.REFUNDED, roles: STAFF }],
  [OrderStatus.RETURNED]: [{ to: OrderStatus.REFUNDED, roles: STAFF }],
  [OrderStatus.REFUNDED]: []
};
var ORDER_STATUSES = Object.values(OrderStatus);
var ORDER_PIPELINE = [
  OrderStatus.PENDING,
  OrderStatus.CONFIRMED,
  OrderStatus.PACKED,
  OrderStatus.SHIPPED,
  OrderStatus.OUT_FOR_DELIVERY,
  OrderStatus.DELIVERED
];
var ORDER_TERMINAL_STATUSES = ORDER_STATUSES.filter(
  (status) => ORDER_TRANSITIONS[status].length === 0
);
var ORDER_CLOSED_STATUSES = [
  OrderStatus.DELIVERED,
  OrderStatus.CANCELLED,
  OrderStatus.RETURNED,
  OrderStatus.REFUNDED
];
var isTerminalOrderStatus = (status) => ORDER_TERMINAL_STATUSES.includes(status);
var isClosedOrderStatus = (status) => ORDER_CLOSED_STATUSES.includes(status);
var orderPipelineIndex = (status) => ORDER_PIPELINE.indexOf(status);
var transitionExists = (from, to) => ORDER_TRANSITIONS[from].some((transition) => transition.to === to);
var canTransition = (from, to, actorRole) => ORDER_TRANSITIONS[from].some(
  (transition) => transition.to === to && transition.roles.includes(actorRole)
);
var allowedTransitions = (from, actorRole) => ORDER_TRANSITIONS[from].filter((transition) => actorRole == null || transition.roles.includes(actorRole)).map((transition) => transition.to);
var assertTransition = (from, to, actorRole) => {
  if (from === to) {
    throw new ConflictError(`The order is already "${to}".`);
  }
  if (!transitionExists(from, to)) {
    throw new ConflictError(`An order cannot move from "${from}" to "${to}".`);
  }
  if (!canTransition(from, to, actorRole)) {
    throw new ForbiddenError(`Role "${actorRole}" cannot move an order from "${from}" to "${to}".`);
  }
};
var canCancelOrder = (from, actorRole) => canTransition(from, OrderStatus.CANCELLED, actorRole);
var isRefundable = (status) => transitionExists(status, OrderStatus.REFUNDED);
var Permission = {
  CATALOG_READ: "catalog:read",
  CATALOG_WRITE: "catalog:write",
  INVENTORY_READ: "inventory:read",
  INVENTORY_WRITE: "inventory:write",
  VEHICLE_READ: "vehicle:read",
  VEHICLE_WRITE: "vehicle:write",
  VEHICLE_TYPE_WRITE: "vehicle_type:write",
  FITMENT_WRITE: "fitment:write",
  GARAGE_MANAGE: "garage:manage",
  CART_MANAGE: "cart:manage",
  CHECKOUT_SUBMIT: "checkout:submit",
  WISHLIST_MANAGE: "wishlist:manage",
  ORDER_CREATE: "order:create",
  ORDER_READ_OWN: "order:read_own",
  ORDER_READ_ALL: "order:read_all",
  ORDER_UPDATE: "order:update",
  ORDER_TRANSITION: "order:transition",
  ORDER_CANCEL_OWN: "order:cancel_own",
  ORDER_CANCEL_ANY: "order:cancel_any",
  PAYMENT_READ: "payment:read",
  REFUND_CREATE: "refund:create",
  SHIPMENT_READ: "shipment:read",
  SHIPMENT_WRITE: "shipment:write",
  CUSTOMER_READ: "customer:read",
  CUSTOMER_WRITE: "customer:write",
  PROMOTION_READ: "promotion:read",
  PROMOTION_WRITE: "promotion:write",
  MEDIA_UPLOAD: "media:upload",
  MEDIA_DELETE: "media:delete",
  REPORT_READ: "report:read",
  NOTIFICATION_READ_OWN: "notification:read_own",
  NOTIFICATION_SEND: "notification:send",
  PROFILE_MANAGE: "profile:manage",
  SETTINGS_READ: "settings:read",
  SETTINGS_WRITE: "settings:write",
  ADMIN_USER_READ: "admin_user:read",
  ADMIN_USER_INVITE: "admin_user:invite",
  ADMIN_USER_WRITE: "admin_user:write",
  AUDIT_LOG_READ: "audit_log:read",
  FEEDBACK_READ: "feedback:read"
};
var ALL_PERMISSIONS = Object.values(Permission);
var CUSTOMER_PERMISSIONS = [
  Permission.CATALOG_READ,
  Permission.VEHICLE_READ,
  Permission.GARAGE_MANAGE,
  Permission.CART_MANAGE,
  Permission.CHECKOUT_SUBMIT,
  Permission.WISHLIST_MANAGE,
  Permission.ORDER_CREATE,
  Permission.ORDER_READ_OWN,
  Permission.ORDER_CANCEL_OWN,
  Permission.NOTIFICATION_READ_OWN,
  Permission.PROFILE_MANAGE
];
var ADMIN_PERMISSIONS = [
  Permission.CATALOG_READ,
  Permission.CATALOG_WRITE,
  Permission.INVENTORY_READ,
  Permission.INVENTORY_WRITE,
  Permission.VEHICLE_READ,
  Permission.VEHICLE_WRITE,
  Permission.FITMENT_WRITE,
  Permission.ORDER_READ_ALL,
  Permission.ORDER_UPDATE,
  Permission.ORDER_TRANSITION,
  Permission.ORDER_CANCEL_ANY,
  Permission.PAYMENT_READ,
  Permission.REFUND_CREATE,
  Permission.SHIPMENT_READ,
  Permission.SHIPMENT_WRITE,
  Permission.CUSTOMER_READ,
  Permission.CUSTOMER_WRITE,
  Permission.PROMOTION_READ,
  Permission.PROMOTION_WRITE,
  Permission.MEDIA_UPLOAD,
  Permission.MEDIA_DELETE,
  Permission.REPORT_READ,
  Permission.NOTIFICATION_READ_OWN,
  Permission.NOTIFICATION_SEND,
  Permission.PROFILE_MANAGE,
  Permission.SETTINGS_READ,
  Permission.FEEDBACK_READ
];
var SUPER_ADMIN_ONLY_PERMISSIONS = [
  Permission.VEHICLE_TYPE_WRITE,
  Permission.SETTINGS_WRITE,
  Permission.ADMIN_USER_READ,
  Permission.ADMIN_USER_INVITE,
  Permission.ADMIN_USER_WRITE,
  Permission.AUDIT_LOG_READ
];
var ROLE_PERMISSIONS = {
  [UserRole.CUSTOMER]: CUSTOMER_PERMISSIONS,
  [UserRole.ADMIN]: ADMIN_PERMISSIONS,
  [UserRole.SUPER_ADMIN]: [...ADMIN_PERMISSIONS, ...SUPER_ADMIN_ONLY_PERMISSIONS]
};
var permissionSets = {
  [UserRole.CUSTOMER]: new Set(ROLE_PERMISSIONS[UserRole.CUSTOMER]),
  [UserRole.ADMIN]: new Set(ROLE_PERMISSIONS[UserRole.ADMIN]),
  [UserRole.SUPER_ADMIN]: new Set(ROLE_PERMISSIONS[UserRole.SUPER_ADMIN])
};
var permissionsFor = (role) => ROLE_PERMISSIONS[role];
var resolveEffectivePermissions = (role, customGrants) => {
  if (role === UserRole.SUPER_ADMIN) return ALL_PERMISSIONS;
  if (customGrants !== null && customGrants !== void 0 && customGrants.length > 0) {
    return customGrants;
  }
  return ROLE_PERMISSIONS[role] ?? [];
};
var can = (role, permission) => permissionSets[role].has(permission);
var canAll = (role, permissions) => permissions.every((permission) => can(role, permission));
var canAny = (role, permissions) => permissions.some((permission) => can(role, permission));
var assertCan = (role, permission) => {
  if (!can(role, permission)) {
    throw new ForbiddenError(`Role "${role}" is not allowed to "${permission}".`);
  }
};
var isStaffRole = (role) => role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN;

export { ALL_PERMISSIONS, AppError, AttributeKind, BASIS_POINTS, ConflictError, FitmentReason, ForbiddenError, HTTP_STATUS_BY_ERROR_CODE, InternalError, NotFoundError, ORDER_CLOSED_STATUSES, ORDER_PIPELINE, ORDER_STATUSES, ORDER_TERMINAL_STATUSES, ORDER_TRANSITIONS, PaymentFailedError, Permission, REDACTED_MESSAGE, ROLE_PERMISSIONS, RateLimitedError, UnauthorizedError, VERTICALS, VERTICAL_SLUGS, ValidationError, allocateProportionally, allowedTransitions, applyPercent, assertCan, assertEnabledVerticals, assertTransition, can, canAll, canAny, canCancelOrder, canTransition, checkFitment, clampToZero, computeLineTotals, computeOrderPricing, couponStatusAt, defaultVertical, enabledVerticals, evaluateCoupon, extractInclusiveTax, filterableAttributes, fitmentSpecificity, fitsVehicle, formatIssuePath, formatMoney, getVertical, groupTax, hasYearConstraint, isAppError, isClosedOrderStatus, isFilterableAttribute, isIndeterminate, isRefundable, isStaffRole, isTerminalOrderStatus, isVerticalSlug, lineGross, listVerticals, matchFitment, matchFitmentToVariant, minPaise, mulDivRound, notFound, orderPipelineIndex, parseMoney, parseOptionalMoney, parseVehicleAttributes, percentToBasisPoints, permissionsFor, quoteShipping, requireVertical, resolveEffectivePermissions, resolveWeightBand, safeParseVehicleAttributes, showsVehicleTypeSelector, sumPaise, toCheckoutTotals, toErrorBody, toTaxLines, totalWeightGrams, transitionExists, validationErrorFromZod, verticalAttributes, yearRangesOverlap, zodIssuesToDetails };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map